import { useEffect, useState } from 'react';
import { getEvents, saveEvent, deleteEvent, getCustomerContacts, GetEventsOutputType, GetCustomerContactsOutputType } from 'zite-endpoints-sdk';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Loader2, CalendarDays } from 'lucide-react';
import { format, parseISO } from 'date-fns';

type Event = GetEventsOutputType['events'][0];
type Contact = GetCustomerContactsOutputType['contacts'][0];

const blank = { eventTitle: '', date: '', time: '', location: '', guestCount: '', notes: '', contactId: '' };

export default function EventsAdminTab() {
  const [events, setEvents] = useState<Event[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ ...blank });
  const [editId, setEditId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    Promise.all([
      getEvents({}),
      getCustomerContacts({}),
    ]).then(([evts, conts]) => {
      setEvents(evts.events);
      setContacts(conts.contacts);
    }).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditId(null); setForm({ ...blank }); setDialogOpen(true); };
  const openEdit = (e: Event) => {
    setEditId(e.id);
    setForm({ eventTitle: e.eventTitle, date: e.date, time: e.time, location: e.location, guestCount: e.guestCount ? String(e.guestCount) : '', notes: e.notes, contactId: e.contactId || '' });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.eventTitle.trim()) { toast.error('Event title is required'); return; }
    setSaving(true);
    try {
      await saveEvent({
        id: editId || undefined,
        eventTitle: form.eventTitle,
        date: form.date || undefined,
        time: form.time || undefined,
        location: form.location || undefined,
        guestCount: form.guestCount ? parseInt(form.guestCount) : undefined,
        notes: form.notes || undefined,
        contactId: form.contactId || undefined,
      });
      toast.success(editId ? 'Event updated' : 'Event created');
      setDialogOpen(false);
      load();
    } catch (e: any) { toast.error(e.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: string) => {
    try { await deleteEvent({ id }); toast.success('Event deleted'); load(); }
    catch { toast.error('Failed to delete'); }
  };

  const fmtDate = (d: string) => {
    try { return format(parseISO(d), 'EEE dd MMM yyyy'); } catch { return d; }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-foreground">Events</h2>
          <p className="text-xs text-muted-foreground">Functions, private hires, live music and special events</p>
        </div>
        <Button onClick={openNew} className="bg-primary text-primary-foreground">
          <Plus className="w-4 h-4 mr-1.5" /> Add Event
        </Button>
      </div>

      {loading ? (
        <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16" />)}</div>
      ) : events.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <CalendarDays className="w-8 h-8 mx-auto mb-2 opacity-40" />
          <p className="text-sm">No events yet. Add your first event.</p>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-lg divide-y divide-border">
          {events.map(e => (
            <div key={e.id} className="px-4 py-3 flex items-center gap-4">
              {e.date && (
                <div className="w-12 text-center bg-muted rounded-md py-1.5 shrink-0">
                  <p className="text-lg font-bold text-primary leading-none">{format(parseISO(e.date), 'd')}</p>
                  <p className="text-xs text-muted-foreground leading-tight">{format(parseISO(e.date), 'MMM')}</p>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-foreground">{e.eventTitle}</p>
                <p className="text-xs text-muted-foreground">
                  {[e.date ? fmtDate(e.date) : '', e.time, e.location, e.guestCount ? `${e.guestCount} guests` : '', e.contactName ? `Contact: ${e.contactName}` : ''].filter(Boolean).join(' · ')}
                </p>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEdit(e)}><Pencil className="w-3 h-3" /></Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive hover:text-destructive"><Trash2 className="w-3 h-3" /></Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader><AlertDialogTitle>Delete "{e.eventTitle}"?</AlertDialogTitle></AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => handleDelete(e.id)}>Delete</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editId ? 'Edit Event' : 'New Event'}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Event Title *</label>
              <Input value={form.eventTitle} onChange={e => setForm(f => ({ ...f, eventTitle: e.target.value }))} placeholder="e.g. Wedding Reception — Barn" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Date</label>
                <Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Time</label>
                <Input value={form.time} onChange={e => setForm(f => ({ ...f, time: e.target.value }))} placeholder="e.g. 2:00 PM" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Location</label>
                <Input value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} placeholder="e.g. The Barn" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Guest Count</label>
                <Input type="number" value={form.guestCount} onChange={e => setForm(f => ({ ...f, guestCount: e.target.value }))} placeholder="80" />
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Contact</label>
              <Select value={form.contactId || 'none'} onValueChange={v => setForm(f => ({ ...f, contactId: v === 'none' ? '' : v }))}>
                <SelectTrigger><SelectValue placeholder="Select contact (optional)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No contact</SelectItem>
                  {contacts.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Notes</label>
              <Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Any additional notes..." className="min-h-16" />
            </div>
            <Button onClick={handleSave} disabled={saving} className="w-full bg-primary text-primary-foreground">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : editId ? 'Update Event' : 'Create Event'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
